import { useEffect, useCallback, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useUser } from "../../context/UserContext";
import { useOnlineStatus } from "../../hooks/useOnlineStatus";
import { useLikers } from "../../hooks/useLikers";
import UserCard from "../UserCard/UserCard";
import { UserCardSkeleton } from "../Skeleton/UserCardSkeleton";
import styles from "./LikesModal.module.scss";

export interface LikesModalProps {
  postId: string;
  open: boolean;
  onClose: () => void;
}

export default function LikesModal({ postId, open, onClose }: LikesModalProps) {
  const [retryKey, setRetryKey] = useState(0);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  // No need to lock body scroll — the overlay covers everything at z-index 1001
  // and has its own overflow-y: scroll

  const handleRetry = () => {
    setRetryKey((k) => k + 1);
  };

  if (!open) return null;

  const modalContent = (
    <div
      className={styles.overlay}
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="likes-modal-title"
    >
      <div className={styles.header}>
        <h2 id="likes-modal-title" className={styles.title}>
          Likes
        </h2>
        <button
          type="button"
          className={styles.closeBtn}
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      <LikesModalBody
        key={retryKey}
        postId={postId}
        overlayRef={overlayRef}
        onClose={onClose}
        onRetry={handleRetry}
      />
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}

function LikesModalBody({
  postId,
  overlayRef,
  onClose,
  onRetry,
}: {
  postId: string;
  overlayRef: React.RefObject<HTMLDivElement | null>;
  onClose: () => void;
  onRetry: () => void;
}) {
  const { user: currentUser } = useUser();
  const { fetchStatuses } = useOnlineStatus();
  const { users, loading, error, hasMore, loadMore, reset } = useLikers(postId, true);

  // Reset hook state on unmount
  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  // Fetch online statuses when users change
  useEffect(() => {
    if (users.length > 0) {
      const userIds = users.map((u) => u.userId);
      fetchStatuses(userIds);
    }
  }, [users, fetchStatuses]);

  // Infinite scroll on the overlay element
  const handleScroll = useCallback(() => {
    const el = overlayRef.current;
    if (!el || !hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight - scrollTop - clientHeight < 200) {
      loadMore();
    }
  }, [overlayRef, hasMore, loading, loadMore]);

  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;

    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [overlayRef, handleScroll]);

  const handleUserCardClick = () => {
    onClose();
  };

  const showSkeleton = loading && users.length === 0;
  const showEmpty = !loading && !error && users.length === 0;
  const showError = !loading && error;

  return (
    <div className={styles.content}>
      {showSkeleton && (
        <div className={styles.userList}>
          <UserCardSkeleton count={8} />
        </div>
      )}

      {showError && (
        <div className={styles.errorState}>
          <p className={styles.errorMessage}>Could not load likes</p>
          <button
            type="button"
            className={styles.retryBtn}
            onClick={onRetry}
          >
            Retry
          </button>
        </div>
      )}

      {showEmpty && (
        <p className={styles.emptyState}>No likes yet</p>
      )}

      {users.length > 0 && (
        <div className={styles.userList}>
          {users.map((liker) => (
            <div
              key={liker.userId}
              onClick={handleUserCardClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleUserCardClick();
                }
              }}
            >
              <UserCard
                user={liker}
                currentUserId={currentUser?.userId}
                initialIsFollowing={liker.isFollowing}
              />
            </div>
          ))}

          {loading && <UserCardSkeleton count={3} />}
        </div>
      )}
    </div>
  );
}
